import React, { useMemo, useState } from 'react';
import {
  FaHeartbeat,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaServer,
  FaMemory,
  FaUsers,
  FaCube,
  FaMicrochip,
} from 'react-icons/fa';
import './PerformancePanel.css';

const HEALTH_META = {
  healthy: { label: 'Saudável', tone: 'ok', Icon: FaCheckCircle },
  warn: { label: 'Atenção', tone: 'warn', Icon: FaExclamationTriangle },
  critical: { label: 'Crítico', tone: 'danger', Icon: FaTimesCircle },
};

const formatNum = (n, digits = 1) => {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString('pt-BR', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return '—';
  }
};

const ageLabel = (seconds) => {
  if (seconds == null) return 'sem dados';
  if (seconds < 5) return 'agora';
  if (seconds < 60) return `há ${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  return `há ${h}h`;
};

function Sparkline({ points, color = 'var(--violet-bright, #a78bfa)', height = 56 }) {
  const series = (points || []).filter((p) => p != null && Number.isFinite(Number(p)));
  if (series.length < 2) {
    return (
      <div className="perf-chart__empty" style={{ height }}>
        Sem histórico ainda
      </div>
    );
  }

  const min = Math.min(...series);
  const max = Math.max(...series);
  const span = max - min || 1;
  const w = 320;
  const pad = 4;
  const coords = series.map((v, i) => {
    const x = pad + (i / (series.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / span) * (height - pad * 2);
    return `${x},${y}`;
  });
  const path = `M ${coords.join(' L ')}`;

  return (
    <svg
      className="perf-chart__svg"
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-hidden="true"
    >
      <path d={path} fill="none" stroke={color} strokeWidth="2" />
    </svg>
  );
}

/**
 * Monitoramento de performance do servidor Minecraft (NexusPerformance).
 */
const PerformancePanel = ({
  latest = null,
  history = [],
  heartbeat = null,
  servers = [],
  loading = false,
  error = '',
  onRefresh,
}) => {
  const [pluginSort, setPluginSort] = useState('score');

  const healthKey = heartbeat?.offline
    ? 'critical'
    : latest?.health || 'healthy';
  const health = HEALTH_META[healthKey] || HEALTH_META.healthy;
  const HealthIcon = health.Icon;

  const tpsHistory = useMemo(
    () => history.map((h) => h?.tps?.one),
    [history]
  );
  const msptHistory = useMemo(
    () => history.map((h) => h?.mspt?.avg),
    [history]
  );

  const plugins = useMemo(() => {
    const list = [...(latest?.plugins || [])];
    list.sort((a, b) => {
      if (pluginSort === 'name') {
        return String(a.name).localeCompare(String(b.name), 'pt-BR');
      }
      if (pluginSort === 'tasks') {
        return (b.tasks || 0) - (a.tasks || 0);
      }
      if (pluginSort === 'listeners') {
        return (b.listenerCount || b.listeners || 0) - (a.listenerCount || a.listeners || 0);
      }
      return (b.score || 0) - (a.score || 0);
    });
    return list;
  }, [latest, pluginSort]);

  const alerts = latest?.alerts || [];

  return (
    <section className="admin-section perf-panel">
      <header className="admin-section__head perf-panel__head">
        <div>
          <h1>
            <FaHeartbeat aria-hidden="true" /> Performance
          </h1>
          <p>
            Monitoramento ao vivo do servidor Minecraft via NexusPerformance
            {latest?.serverId ? ` · ${latest.serverId}` : ''}
            {servers.length > 1 ? ` · ${servers.length} servidores` : ''}
          </p>
        </div>
        <div className="perf-panel__head-actions">
          <button
            type="button"
            className="admin-btn admin-btn--ghost admin-btn--sm"
            onClick={onRefresh}
            disabled={loading}
          >
            <FaSync className={loading ? 'admin-spin' : ''} aria-hidden="true" />
            Atualizar
          </button>
        </div>
      </header>

      {error ? <p className="admin-banner admin-banner--error">{error}</p> : null}

      <div className={`perf-status perf-status--${health.tone}`}>
        <div className="perf-status__main">
          <HealthIcon aria-hidden="true" />
          <div>
            <strong>{health.label}</strong>
            <span>
              {heartbeat?.offline
                ? 'Plugin offline — sem push recente'
                : heartbeat?.online
                  ? `Online · último push ${ageLabel(heartbeat.ageSeconds)}`
                  : 'Aguardando primeiro push do plugin'}
            </span>
          </div>
        </div>
        <div className="perf-status__meta">
          <span>
            <FaServer aria-hidden="true" /> {formatDate(latest?.collectedAt)}
          </span>
          {latest?.meta?.pluginVersion ? (
            <span>NexusPerformance {latest.meta.pluginVersion}</span>
          ) : null}
          {latest?.meta?.collectMicros != null ? (
            <span>Coleta {formatNum(latest.meta.collectMicros / 1000, 2)} ms</span>
          ) : null}
        </div>
      </div>

      {!latest ? (
        <div className="perf-empty">
          <FaServer aria-hidden="true" />
          <h2>Nenhum dado ainda</h2>
          <p>
            Instale o plugin NexusPerformance no servidor, configure o
            PERFORMANCE_BRIDGE_SECRET e aguarde o primeiro push (~15s).
          </p>
        </div>
      ) : (
        <>
          <div className="perf-cards">
            <article className="perf-card">
              <header>
                <FaMicrochip aria-hidden="true" /> TPS
              </header>
              <strong>{formatNum(latest.tps?.one, 2)}</strong>
              <span>
                5m {formatNum(latest.tps?.five, 2)} · 15m{' '}
                {formatNum(latest.tps?.fifteen, 2)}
              </span>
            </article>
            <article className="perf-card">
              <header>
                <FaHeartbeat aria-hidden="true" /> MSPT
              </header>
              <strong>{formatNum(latest.mspt?.avg, 1)} ms</strong>
              <span>p95 {formatNum(latest.mspt?.p95, 1)} ms</span>
            </article>
            <article className="perf-card">
              <header>
                <FaMemory aria-hidden="true" /> Memória
              </header>
              <strong>{formatNum(latest.memory?.usedPercent, 0)}%</strong>
              <span>
                {formatNum(latest.memory?.usedMb, 0)} /{' '}
                {formatNum(latest.memory?.maxMb, 0)} MB
              </span>
            </article>
            <article className="perf-card">
              <header>
                <FaUsers aria-hidden="true" /> Players
              </header>
              <strong>
                {latest.players?.online ?? 0}
                <small>/{latest.players?.max ?? '—'}</small>
              </strong>
              <span>
                CPU{' '}
                {latest.cpuProcessPercent != null
                  ? `${formatNum(latest.cpuProcessPercent, 0)}%`
                  : '—'}
              </span>
            </article>
            <article className="perf-card">
              <header>
                <FaCube aria-hidden="true" /> Mundo
              </header>
              <strong>{formatNum(latest.entities, 0)}</strong>
              <span>
                entidades · {formatNum(latest.chunksLoaded, 0)} chunks
              </span>
            </article>
            <article className="perf-card">
              <header>
                <FaSync aria-hidden="true" /> GC
              </header>
              <strong>{latest.gc?.countDelta ?? 0}</strong>
              <span>
                ciclos · {formatNum(latest.gc?.timeMsDelta, 0)} ms neste intervalo
              </span>
            </article>
          </div>

          <div className="perf-charts">
            <article className="perf-chart">
              <header>
                <h2>TPS (histórico)</h2>
                <span>{history.length} pontos</span>
              </header>
              <Sparkline points={tpsHistory} color="#34d399" />
            </article>
            <article className="perf-chart">
              <header>
                <h2>MSPT (histórico)</h2>
                <span>média por amostra</span>
              </header>
              <Sparkline points={msptHistory} color="#fbbf24" />
            </article>
          </div>

          {alerts.length > 0 ? (
            <div className="perf-alerts">
              <h2>
                <FaExclamationTriangle aria-hidden="true" /> Alertas atuais
              </h2>
              <ul>
                {alerts.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {(latest.worlds || []).length > 0 ? (
            <div className="perf-worlds">
              <h2>Mundos</h2>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Mundo</th>
                      <th>Players</th>
                      <th>Entidades</th>
                      <th>Chunks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {latest.worlds.map((w) => (
                      <tr key={w.name}>
                        <td>{w.name}</td>
                        <td>{w.players ?? 0}</td>
                        <td>{w.entities ?? 0}</td>
                        <td>{w.chunks ?? 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}

          <div className="perf-plugins">
            <header className="perf-plugins__head">
              <div>
                <h2>Plugins</h2>
                <p>
                  Ranking por custo estimado (tasks + listeners). Não é um
                  profiler bytecode — é uma amostragem leve e segura.
                </p>
              </div>
              <label>
                Ordenar
                <select
                  value={pluginSort}
                  onChange={(e) => setPluginSort(e.target.value)}
                >
                  <option value="score">Score</option>
                  <option value="tasks">Tasks</option>
                  <option value="listeners">Listeners</option>
                  <option value="name">Nome</option>
                </select>
              </label>
            </header>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Plugin</th>
                    <th>Versão</th>
                    <th>Tasks</th>
                    <th>Listeners</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {plugins.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="admin-table__empty">
                        Sem dados de plugins neste snapshot.
                      </td>
                    </tr>
                  ) : (
                    plugins.map((p) => {
                      const flags = p.flags || [];
                      const tone = flags.includes('heavy')
                        ? 'danger'
                        : flags.includes('suspect')
                          ? 'warn'
                          : 'ok';
                      return (
                        <tr key={p.name} className={!p.enabled ? 'is-disabled' : ''}>
                          <td>
                            <strong>{p.name}</strong>
                          </td>
                          <td>{p.version || '—'}</td>
                          <td>{p.tasks ?? 0}</td>
                          <td>{p.listenerCount ?? p.listeners ?? 0}</td>
                          <td>{formatNum(p.score, 0)}</td>
                          <td>
                            <span className={`perf-flag perf-flag--${tone}`}>
                              {!p.enabled
                                ? 'Desligado'
                                : flags.includes('heavy')
                                  ? 'Pesado'
                                  : flags.includes('suspect')
                                    ? 'Suspeito'
                                    : 'OK'}
                            </span>
                          </td>
                          <td className="perf-plugins__reason">
                            {(p.reasons || []).join(' · ') || '—'}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default PerformancePanel;
